import type { ApiResponse } from '@/types'
import dayjs, { type Dayjs } from 'dayjs'

/** Lifecycle status of a locally uploaded POD attachment. */
export type PodUploadStatus = 'Pending' | 'Approved' | 'Rejected'

/** SAP transporter document row (CPI field names vary; we normalize keys for the table). */
export type TransporterSapRow = Record<string, unknown> & {
  DocumentNo?: string | number
  DocType?: string
  Quantity?: string | number
  PostingDate?: string
  CustomerCode?: string
  CustomerName?: string
  TransporterCode?: string
  TransporterName?: string
  VehicleNo?: string
  FromLocation?: string
  FL_Name?: string
  ToLocation?: string
  TL_Name?: string
  LR_Number?: string
  LR_No?: string
  VendorCode?: string | number
  Vendor_Code?: string | number
  Lifnr?: string | number
  VendorName?: string
  Vendor_Name?: string
  Vendor?: string
  has_document?: boolean | string | number
  /**
   * Reconciliation metadata added by the backend (Option B flat union):
   * - 'sap'    → a SAP "to upload" document (podStatus is null)
   * - 'upload' → one locally uploaded POD attachment with its own status
   */
  rowType?: 'sap' | 'upload'
  approvalId?: number
  podStatus?: PodUploadStatus | null
  /** SAP row only: whether the transporter may upload (no Pending/Approved attachment). */
  canUpload?: boolean
  attachmentUrl?: string | null
  approval_remark?: string | null
  rejection_remark?: string | null
  remarks?: string | null
  latestRejectionRemark?: string | null
  createdAt?: string
}

/**
 * Resolves location code and name pair, handling SAP CPI edge cases (e.g. ALT doc type
 * returning swapped FromLocation [Name] and FL_Name [Code] values).
 */
export function resolveLocationPair(
  rawCode: unknown,
  rawName: unknown,
  isExplicitlySwapped = false
): { code: string; name: string } {
  const c = String(rawCode ?? '').trim()
  const n = String(rawName ?? '').trim()

  if (!c && !n) return { code: '', name: '' }
  if (!n) return { code: c, name: c }
  if (!c) return { code: n, name: n }

  // Detect if fields are swapped in SAP payload (e.g. ALT doc type FromLocation):
  const codeHasSpacesOrLong = c.includes(' ') || c.length > 20
  const nameIsShortCode = !n.includes(' ') && n.length <= 15
  const isSwapped = isExplicitlySwapped || (codeHasSpacesOrLong && nameIsShortCode)

  if (isSwapped) {
    return { code: n, name: c }
  }

  return { code: c, name: n }
}

export function resolveFromLocation(r: Record<string, unknown>): { code: string; name: string } {
  const dt = String(r.DocType || r.type || '').toUpperCase()
  return resolveLocationPair(r.FromLocation, r.FL_Name, dt === 'ALT')
}

export function resolveToLocation(r: Record<string, unknown>): { code: string; name: string } {
  return resolveLocationPair(r.ToLocation, r.TL_Name, false)
}

export function resolveTransporterCode(user: {
  transporterCode?: string
  iam?: Record<string, unknown>
} | null | undefined): string {
  const direct = user?.transporterCode?.trim()
  if (direct) return direct

  const iam = user?.iam
  if (!iam || typeof iam !== 'object') return ''

  const companies = iam.companies
  if (Array.isArray(companies) && companies[0] && typeof companies[0] === 'object') {
    const c0 = companies[0] as Record<string, unknown>
    const bp = c0.bpCode ?? c0.bp_code ?? c0.code
    if (typeof bp === 'string' && bp.trim()) return bp.trim()
  }

  const cc = iam.customer_codes ?? iam.customerCodes
  if (Array.isArray(cc) && cc[0]) return String(cc[0]).trim()
  if (typeof cc === 'string' && cc.trim()) return cc.trim()

  const bp = iam.bp_code ?? iam.bpCode
  if (typeof bp === 'string' && bp.trim()) return bp.trim()

  return ''
}

export function extractTransporterSapRows(
  response: ApiResponse<unknown> | { data?: unknown; legacy?: unknown } | unknown
): TransporterSapRow[] {
  const body = response as Record<string, unknown> | null | undefined
  if (!body) return []

  if (Array.isArray(body)) return body as TransporterSapRow[]

  if (Array.isArray(body.data)) return body.data as TransporterSapRow[]

  const legacy = body.legacy as Record<string, unknown> | undefined
  const legacyRd = legacy?.Response_Data as Record<string, unknown> | undefined
  if (Array.isArray(legacyRd?.Response)) return legacyRd.Response as TransporterSapRow[]

  const data = body.data as Record<string, unknown> | undefined
  if (data && typeof data === 'object') {
    const rd = data.Response_Data as Record<string, unknown> | undefined
    if (Array.isArray(rd?.Response)) return rd.Response as TransporterSapRow[]
  }

  return []
}

export function sapDateRangeToRequest(
  range: [Dayjs, Dayjs] | null | undefined
): { fromDate: string; toDate: string } {
  const from = range?.[0] ?? dayjs().subtract(3, 'month')
  const to = range?.[1] ?? dayjs()
  return {
    fromDate: from.format('DD/MM/YYYY'),
    toDate: to.format('DD/MM/YYYY'),
  }
}

export function rowKey(row: TransporterSapRow, index: number): string {
  const doc = row.DocumentNo ?? row.documentNo ?? index
  const typ = row.DocType ?? row.type ?? ''
  return `${doc}-${typ}-${index}`
}
